import type { ComponentPropsWithoutRef } from "react";

import { CommandMan, CommandType, State, type Damper as DamperType } from "@eisox/api/core-api";
import { cn } from "@eisox/design-system";

import WarningIcon from "~/assets/boilerroomWarning.svg?react";
import Commutator from "~/assets/commutator.svg?react";
import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";

import { getActuatorState, isAutoManualOrForcing } from "../helpers";
import { Direction, Position, type Component } from "../types";
import { Card } from "./card";

interface DamperProps extends Omit<DamperType, "id">, Component, ComponentPropsWithoutRef<"div"> {}

const Damper: React.FC<DamperProps> = ({
  state: stateProp,
  command,
  commandAutomate,
  commandMan,
  commandType,
  defect = false,
  missmatchDefect = false,
  position = Position.TOP,
  direction = Direction.RIGHT,
  ...props
}) => {
  const { state, warning } = getActuatorState({ state: stateProp, commandMan, commandAutomate, defect });
  const autoManualOrForcing = isAutoManualOrForcing(commandMan, command);

  const opening = (commandType === CommandType.PERCENTAGE ? state : state === State.ON ? 100 : 0) ?? 45;
  const rotationAngle = (opening / 100) * 90 + 90;

  const svgTransform =
    [direction === Direction.LEFT && "scaleX(-1)", position === Position.BOTTOM && "scaleY(-1)"]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div
      className={cn("flex w-fit flex-col items-center", position === Position.BOTTOM && "flex-col-reverse")}
      {...props}
      style={{ cursor: props.onClick ? "pointer" : "default" }}
    >
      <Card defect={defect || missmatchDefect} position={position}>
        {commandMan === CommandMan.MANUAL && <Commutator />}
        {commandType === CommandType.PERCENTAGE &&
          `${m.actuator_state({ state: autoManualOrForcing })} - ${opening.toFixed(1)} %`}
        {commandType === CommandType.ALL_OR_NOTHING &&
          `${m.actuator_state({ state: autoManualOrForcing })} - ${state === State.ON ? m.open() : m.closed()}`}
        {warning && (
          <Tooltip content={warning}>
            <WarningIcon />
          </Tooltip>
        )}
      </Card>
      <svg
        width="29"
        height="97"
        viewBox="0 0 29 97"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={svgTransform ? { transform: svgTransform } : undefined}
      >
        <rect x="1" y="5.5" width="27" height="91" rx="4.5" fill="#F3F3F9" />
        <rect x="1" y="5.5" width="27" height="91" rx="4.5" stroke="#122138" />
        <g>
          <rect
            x="3.5"
            y="15.5"
            width="22"
            height="3"
            rx="1.5"
            transform={`rotate(-${rotationAngle} 14.5 17)`}
            fill="#122138"
          />
          <circle cx="14.5" cy="17" r="2.5" fill="#122138" />
        </g>
        <g>
          <rect
            x="3.5"
            y="38.1667"
            width="22"
            height="3"
            rx="1.5"
            transform={`rotate(${rotationAngle} 14.5 39.6667)`}
            fill="#122138"
          />
          <circle cx="14.5" cy="39.6667" r="2.5" fill="#122138" />
        </g>
        <g>
          <rect
            x="3.5"
            y="60.8333"
            width="22"
            height="3"
            rx="1.5"
            transform={`rotate(-${rotationAngle} 14.5 62.3333)`}
            fill="#122138"
          />
          <circle cx="14.5" cy="62.3333" r="2.5" fill="#122138" />
        </g>
        <g>
          <rect
            x="3.5"
            y="83.5"
            width="22"
            height="3"
            rx="1.5"
            transform={`rotate(${rotationAngle} 14.5 85)`}
            fill="#122138"
          />
          <circle cx="14.5" cy="85" r="2.5" fill="#122138" />
        </g>
        <rect x="13.5" y="0" width="2" height="96" fill="#122138" />
      </svg>
    </div>
  );
};

Damper.displayName = "Damper";

export { Damper };
export type { DamperProps };
